import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Globe, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import ScoreBadge from './ScoreBadge';
import { DetailCard } from './DetailCard';

const GeoAnalyzerTab = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const analyze = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let pageContent = '';
      try {
        const res = await fetch(url);
        pageContent = await res.text();
      } catch {
        pageContent = `[Could not fetch — URL provided: ${url}]`;
      }

      const { data, error: fnErr } = await supabase.functions.invoke('geo-optimize', {
        body: { pageContent, pageUrl: url },
      });

      if (fnErr) throw fnErr;
      setResult(typeof data === 'string' ? JSON.parse(data) : data);
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="w-5 h-5 text-primary" />
            GEO Analyzer
          </CardTitle>
          <CardDescription>Analyze any page for AI citability and Generative Engine Optimization readiness.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="geo-url">Page URL</Label>
              <Input
                id="geo-url"
                placeholder="https://example.com/page"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && analyze()}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={analyze} disabled={loading || !url.trim()}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Analyze
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-border border-red-500">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500">{error}</span>
          </CardContent>
        </Card>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <ScoreBadge score={result.overallScore} label="Overall" />
            <ScoreBadge score={result.semanticRichness?.score} label="Semantic" />
            <ScoreBadge score={result.authoritySignals?.score} label="Authority" />
            <ScoreBadge score={result.schemaCompleteness?.score} label="Schema" />
            <ScoreBadge score={result.aiCitability?.score} label="AI Citability" />
          </div>

          {result.topPriorityActions?.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Top Priority Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  {result.topPriorityActions.map((a: string, i: number) => (
                    <li key={i}>{a}</li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {result.semanticRichness?.suggestions?.length > 0 && (
              <DetailCard title="Semantic Richness" items={result.semanticRichness.suggestions} />
            )}
            {result.authoritySignals?.suggestions?.length > 0 && (
              <DetailCard title="Authority Signals" items={result.authoritySignals.suggestions} />
            )}
            {result.schemaCompleteness?.suggestions?.length > 0 && (
              <DetailCard title="Schema Suggestions" items={result.schemaCompleteness.suggestions} />
            )}
            {result.aiCitability?.improvements?.length > 0 && (
              <DetailCard title="AI Citability Improvements" items={result.aiCitability.improvements} />
            )}
          </div>

          {(result.longTailKeywords?.found?.length > 0 || result.longTailKeywords?.recommended?.length > 0) && (
            <Card className="border-border">
              <CardHeader><CardTitle className="text-base">Long-Tail Keywords</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {result.longTailKeywords.found?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase mb-1 text-muted-foreground">Found</p>
                    <div className="flex flex-wrap gap-2">
                      {result.longTailKeywords.found.map((k: string, i: number) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
                {result.longTailKeywords.recommended?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase mb-1 text-muted-foreground">Recommended</p>
                    <div className="flex flex-wrap gap-2">
                      {result.longTailKeywords.recommended.map((k: string, i: number) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default GeoAnalyzerTab;
